import 'primeicons/primeicons.css'
import './styles.css'

import { MutationCache, QueryCache, QueryClient, VueQueryPlugin } from '@tanstack/vue-query'
import { createPinia } from 'pinia'
import PrimeVue from 'primevue/config'
import ConfirmationService from 'primevue/confirmationservice'
import ToastService from 'primevue/toastservice'
import { createApp } from 'vue'
import { config as zodConfig } from 'zod'
import { ApiProblem, configureAuth, onConnectivity } from '@/shared/api'
import { reportConnectivity } from '@/shared/composables/useOnline'
import { useSessionStore } from '@/shared/session'
import { notify } from '@/shared/utils/notify'
import { followSystemTheme } from '@/shared/utils/theme'

import App from './App.vue'
import { i18n } from './i18n'
import { primeVueLocaleRu } from './primevue-locale-ru'
import { router } from './router'
import { setupPwa } from './pwa'
import { primeVueThemeOptions } from './theme'

// Zod проверяет, можно ли `new Function`, и под нашей CSP (без 'unsafe-eval') браузер
// записывает это в консоль как нарушение. Без JIT схемы работают так же.
zodConfig({ jitless: true })

followSystemTheme()

const app = createApp(App)
const pinia = createPinia()
app.use(pinia)

const session = useSessionStore(pinia)
configureAuth({
  token: () => session.accessToken,
  refresh: () => session.refresh(),
  expired: () => {
    // Обновить сессию не дала сеть, а не сервер: пользователя не выкидываем (ТЗ 18.7).
    if (session.status !== 'guest') return
    session.signOut()
    void router.replace({ name: 'login', query: { redirect: router.currentRoute.value.fullPath } })
  },
  mfaSetupRequired: () => {
    session.mfaSetupRequired = true
    void router.replace({ name: 'settings-security' })
  },
})
onConnectivity(reportConnectivity)

/** Сбои сети и сервера показываются уведомлением, остальное обрабатывают экраны (ТЗ 18.4). */
function reportFailure(error: unknown): void {
  if (!(error instanceof ApiProblem)) return
  const t = i18n.global.t
  if (error.isNetwork) notify('error', t('problems.network'))
  else if (error.status >= 500) notify('error', t('problems.internal_error'))
}

const queryClient = new QueryClient({
  queryCache: new QueryCache({ onError: reportFailure }),
  mutationCache: new MutationCache({ onError: reportFailure }),
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      // Ошибки клиента (403, 404, 422) не повторяются, сетевые и 5xx один раз.
      retry: (count, error) =>
        count < 1 && (!(error instanceof ApiProblem) || error.isNetwork || error.status >= 500),
    },
  },
})

app.use(router)
app.use(i18n)
app.use(VueQueryPlugin, { queryClient })
app.use(PrimeVue, {
  theme: primeVueThemeOptions,
  locale: primeVueLocaleRu,
  ripple: false,
  pt: {
    // У индикатора загрузки роль progressbar, ей нужно имя для экранного диктора (WCAG 4.1.2).
    progressspinner: { root: { 'aria-label': i18n.global.t('common.loading') } },
    // Password вешает aria-expanded и aria-haspopup на поле type="password", где они
    // недопустимы (axe aria-allowed-attr). Подсказка о надёжности озвучивается и без них.
    password: { pcInputText: { root: { 'aria-expanded': null, 'aria-haspopup': null } } },
  },
})
app.use(ToastService)
app.use(ConfirmationService)

app.mount('#app')
setupPwa(router)
